import type { RouteLocationRaw, Router } from 'vue-router';
import { waitForRouteReady } from '@/autopilot/routeReadiness';
import type { AutopilotFunctionFactory } from './types';

/** Push a route and wait until the destination page has released the readiness lock. */
export async function pushAndWait(router: Router, to: RouteLocationRaw): Promise<void> {
  await router.push(to);
  await waitForRouteReady();
}

interface NavDestination {
  key: string;
  label: string;
  to: RouteLocationRaw;
}

/**
 * Main-section destinations the assistant can send the user to. Keys stay stable (not translated)
 * so the function schema does not churn with locale. Feature-gated pages are still listed; the
 * destination page itself enforces access.
 */
export function collectNavigateDestinations(): NavDestination[] {
  return [
    { key: 'home', label: 'Home', to: { name: 'home' } },
    { key: 'orders', label: 'Orders', to: { name: 'orders.list' } },
    { key: 'order-create', label: 'Create order', to: { name: 'order.create' } },
    { key: 'poolmirror', label: 'Poolmirror campaigns', to: { name: 'poolmirror.list' } },
    { key: 'promptsuite', label: 'Promptsuite apps', to: { name: 'promptsuite.apps' } },
    { key: 'cvanalyzer', label: 'CV analyzer', to: { name: 'cvanalyzer.list' } },
    { key: 'external-job-markets', label: 'External job markets', to: { name: 'external_job_markets.list' } },
    { key: 'billing', label: 'Billing', to: { name: 'billing.budget' } },
    { key: 'virtual-budgets', label: 'Virtual budgets', to: { name: 'billing.vbudgets' } },
    { key: 'settings', label: 'Settings', to: { name: 'settings' } },
    { key: 'accounts', label: 'Accounts', to: { name: 'settings.accounts' } },
    { key: 'companies', label: 'Companies', to: { name: 'settings.companies' } },
    { key: 'networks', label: 'Networks', to: { name: 'settings.networks' } },
    { key: 'swimlanes', label: 'Swimlanes', to: { name: 'settings.swimlanes' } },
    { key: 'candidate-fields', label: 'Candidate fields', to: { name: 'settings.candidate_fields' } },
  ];
}

/**
 * Exposes the main navigation to the autopilot as a single `navigate` function so the
 * assistant can route the user to any main section of the app.
 */
export const createNavigateFunction: AutopilotFunctionFactory = ({ router }) => {
  const destinations = collectNavigateDestinations();
  const byKey = new Map(destinations.map((d) => [d.key, d]));

  return {
    name: 'navigate',
    description:
      'Navigate the user to one of the main sections of the application. ' +
      'Available destinations: ' +
      destinations.map((d) => `"${d.key}" (${d.label})`).join(', ') +
      '. This only changes the current page; it does not create or save anything.',
    parameters: {
      type: 'object',
      properties: {
        destination: {
          type: 'string',
          enum: destinations.map((d) => d.key),
          description: 'The destination key to navigate to.',
        },
      },
      required: ['destination'],
    },
    handler: async (rawArgs) => {
      const args = (rawArgs ?? {}) as { destination?: string };
      const dest = args.destination ? byKey.get(args.destination) : undefined;
      if (!dest) {
        return { success: false, error: `Unknown destination: ${args.destination ?? '(none)'}` };
      }
      await pushAndWait(router, dest.to);
      return { success: true, destination: dest.key, label: dest.label };
    },
  };
};
